import React, {useState, useEffect, useRef, useCallback} from 'react';
import {renderToString} from 'react-dom/server';
import PropTypes from 'prop-types';
import {Spinner, InputGroup} from 'react-bootstrap';
import * as Yup from "yup";
import geojsonhint from "@mapbox/geojsonhint";
import {genRanHex, IsJsonValid} from '../utils'
import mergeWith from 'lodash/mergeWith';
import isArray from "lodash/isArray";

Yup.addMethod(Yup.number, 'allowEmptyString', function () {
  return this.transform(function (value, originalValue) {
    return isNaN(value) && (originalValue === '' || originalValue === null ) ? undefined : value
  });
});


Yup.addMethod(Yup.boolean, 'allowEmptyString', function () {
  return this.transform(function (value, originalValue) {
    return (typeof value != "boolean") && (originalValue === '' || originalValue === null ) ? undefined : value
  });
});

Yup.addMethod(Yup.string, 'geojson', function (message) {
  return this.test('isGeojsonValid', message, function(value) {
    const { path, createError } = this;
    const errors = geojsonhint.hint(value)
    return errors.length === 0 || createError({ path, message: errors[0].message })
  })
});

Yup.addMethod(Yup.object, 'geojson', function (message) {
  return this.test('isGeojsonValid', message, function(value) {
    const { path, createError } = this;
    const errors = geojsonhint.hint(JSON.stringify(value))
    return errors.length === 0 || createError({ path, message: errors[0].message })
  })
});

Yup.addMethod(Yup.string, 'json', function (message) {
  return this.test('isJsonValid', message, function(value) {
    const { path, createError } = this;
    return IsJsonValid(value) || createError({ path, message: "JSON is invalid" })
  })
});


export {Yup}

/**
 * useForm is a custom form hook that handles controlled inputs, validations, submission, etc.
 * @param  {Object} [defaultObject=null] - The default object used to initialize the form if available when creating the hook
 *                                         Can be omitted, defaults to null
 * @param  {Object} initialValues - An object with one key per form attribute
 *                                  If the key matches an object attribute, the value should be the default value for that attribute
 *                                  (as inputs are controlled, this can't be null - empty string for text or number inputs for example)
 *                                  If the key does not match an object attribute, the value should be a function with the
 *                                  object as an argument and should return a default value if the object is null:
 *                                  (obj) => obj.value || ''
 * @param {Yup Schema} [validationSchema] - A Yup validation schema that will be used to validate each form input. The keys must match
 *                                          the ones passed in the initialValues prop. If an input has no key, it will not be validated
 * @param {function} [onValidation] - A function called before validation, pass formData as argument
 * @param {function} [onValidationFail] - A function called when validation fail
 * @param {function} onSubmit - A function that will be called on form submission with form data as an argument, only if validation is successful
 *                              Only called for a parent form - ie, will not be called if a parentFormRef is set
 * @param {Object}  [parentFormRef] - A reference to a parent form only in the case of nested forms. The object to use is
 *                                    obtained by calling the method returned as useParentFormRef from the parent's form useForm hook initialization,
 *                                    with the name of the child form as argument. This name must match a key in initialValues
 *                                    Only one level of form nesting is allowed
 * @param {function} [preProcess] - A function that will be called before validation with form data as an argument.
 *                                  Allows for data transformation before validation, and especially useful for child forms,
 *                                  where onSubmit will never be called
 * @param {Object} [onAttributeChange] - The key is the attribute name (matching one in initialValues) whose change should trigger a change in another attribute.
 *                                      The value is an Object as well, with a key (matching one in initialValues) per attribute to update,
 *                                      and a function to update this value, with the changed value as argument
 * @param {boolean} [disabled=false] - If the form is disabled or enabled - allows to automatic disabling of form inputs
 *
 * @return {Object} formData - The form data
 * @return {function} setFormData - Should be called to update form data, when a new object is received from sever for example
 * @return {function} getInputProps - Called with the name of an input as argument (matching one of initialValues above),
 *                                    returns a set of props for that input, to be included like that:
 *                                    {...getInputProps("name")}
 *                                    Should be added to every input of the form
 * @return {Object} formProps - A set of props to be applied to the Form component
 * @return {Object} submitButtonProps - A set of props to be applied to the submit Button component
 * @return {function} useParentFormRef - A function to be used passed down a reference to child forms
 * @return {function} setDisabled - To be called with a boolean as an argument
 * @return {boolean} validated - True if the form has already been submitted and validated (successfully or not)
 */

export function useForm({defaultObject = null, initialValues, validationSchema, onValidation = f=>f, onValidationFail = f=>f, onSubmit, parentFormRef, preProcess, onAttributeChange, ...props}) {
  // do not call function
  // https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
  const [formData, setInnerFormData] = useState(buildFormObject(defaultObject));
  const [errors, setErrors] = useState({});
  const [validated, setValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [childForms, setChildForms] = useState([])
  const [disabled, setDisabled] = useState(props.disabled)

  const submitButtonRef = useRef(null);
  const [formId,] = useState(genRanHex());

  function buildFormData(object) {
    let output = {}
    for (const [key, value] of Object.entries(initialValues)) {
      if (typeof value === 'function') {
        output[key] = value(object)
      } else if (object && object[key] != null) {
        output[key] = object[key]
      } else {
        output[key] = value
      }
    }
    return output;
  }

  function buildFormObject(object) {
    const data = buildFormData(object)
    let newObject = {};
    Object.keys(data).forEach((key) => {
      if (data[key] !== undefined) { newObject[key] = data[key] }
    });
    return newObject;
  }

  function handleInputChange(value, propName) {
    setInnerFormData(prevFormData => ({...prevFormData, [propName]: value}));
  }

  function getInputValue(event) {
    const target = event && event.target;
    let value, name;
    if (target) {
      value = target.type === 'checkbox' ? target.checked : target.value;
    } else {
      value = event;
    }
    return value
  }

  function setFormData(data) {
    setInnerFormData(buildFormObject(data))
  }

  function getDisabledValue(arg) {
    if (typeof props.disabled === 'function') { return props.disabled(arg) }
    return disabled
  }

  function getInputProps(propName) {
    let validationProps = {}
    if (validated && !getDisabledValue(propName)) {
      if (errors[propName]) {
        validationProps = {
          errors: errors[propName],
          isInvalid: true
        }
      } else {
        validationProps = {
          isValid: true
        }
      }
    }

    return {
      name: propName,
      onChange: (event) => {
        const value = getInputValue(event)
        if (onAttributeChange && onAttributeChange[propName]) {
          for (const [key, v] of Object.entries(onAttributeChange[propName])) {
            if (v(value) !== false) {
              handleInputChange(v(value), key)
            }
          }
        }
        handleInputChange(value, propName)
      },
      value: formData[propName],
      disabled: getDisabledValue(propName),
      ...validationProps
    }
  }

  const onFormSubmit = (e) => {
    e.stopPropagation()
    e.preventDefault();
    Promise.all(childForms.map(x => x.validate({name: x.name})))
      .then(status => {
        // Set null for each property covered by a child form
        const parentFormData = status.flatMap(el => Object.keys(el.data || {})).reduce((acc, value) => {
          return {...acc, [value]: null}
        }, formData)
        const mergedStatus = status.reduce((acc, value) => {
          const mergedData = mergeWith(acc.data, value.data, (objValue, srcValue) => {
            if (isArray(objValue) && isArray(srcValue)) {
              return objValue.concat(srcValue);
            }
          })
          // Set child  form data, merge if property is an array (important to preprocess such property in useForm in an Array)
          // This explains the need to filter formData above, to avoid duplicates
          return {data: mergedData, valid: acc.valid && value.valid}
        }, {data: parentFormData, valid: true})
        return validate(mergedStatus)
      })
      .then(status => {
        if (status.valid) {
          onSubmit(status.data)
        }
      })
  }

  // For now, only one level of nesting is allowed
  // Next step: make the validation recursive
  const validate = ({valid = true, name, data = {}}) => {
    setIsValidating(true)
    let validationPromise
    onValidation(formData)
    if (validationSchema) {
      validationPromise = validationSchema.validate(formData, {abortEarly: false})
    } else {
      validationPromise = new Promise(function(resolve, reject) {
        resolve(formData)
      })
    }

    return new Promise(function (resolve, reject) {
      validationPromise.then(value => {
        setErrors({})
        const processedData = preProcess ? preProcess(value) : value
        const validatedValue = name ? {[name]: processedData} : {...processedData}
        resolve({data: {...validatedValue, ...data}, valid: valid})
      })
      .catch(e => {
        const errors = e.inner.reduce((res, obj) => {
          if (res.hasOwnProperty(obj.path)) {
            res[obj.path].push(obj.message)
          } else {
            res[obj.path] = [obj.message]
          }
          return res
        }, {})
        setErrors(errors)
        resolve({data: null, valid: false})
      })
      .finally( () => {
        setIsValidating(false)
        setValidated(true)
      })
    })
  }

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      onValidationFail(errors)
    }
  }, [JSON.stringify(errors)])

  // called from child
  useEffect(() => {
    if (parentFormRef) {
      parentFormRef.register({
        name: parentFormRef.name,
        id: formId,
        validate: validate
      })
      return () => parentFormRef.deregister(formId)
    }
  }, [formData])

  // given by parent
  const useParentFormRef = (name) => {
    return {
      name: name,
      register: (childForm) => {
        setChildForms(existingChildForms => {
          const index = existingChildForms.findIndex(x => x.id === childForm.id)
          if (index >= 0) {
            const childFormsCopy = existingChildForms.slice()
            childFormsCopy[index] = childForm
            return childFormsCopy
          } else {
            return [...existingChildForms, childForm]
          }
        })
      },
      deregister: (id) => {
        setChildForms(existingChildForms => {
          const index = existingChildForms.findIndex(x => x.id === id)
          if (index >= 0) {
            let childFormsCopy = existingChildForms.slice()
            childFormsCopy.splice(index, 1)
            return childFormsCopy
          } else {
            return existingChildForms
          }
        })
      }
    }
  }

  const formProps = {
    noValidate: true,
    onSubmit: onFormSubmit,
    id: formId
  }

  const submitButtonProps = {
    type: "submit",
    ref: submitButtonRef,
    variant: "primary",
    form: formId
  }

  useEffect(() => {
    const button = submitButtonRef.current
    if (button) {
      if (!button.dataset.content) {
        button.dataset.content = button.innerHTML
        button.style.width = `${button.offsetWidth}px`
      }
      if (isValidating) {
        button.innerHTML = renderToString(<Spinner animation="border" style={{width: "1rem", height:"1rem"}}/>)
        button.blur()
      } else {
        button.innerHTML = button.dataset.content
      }
    }
  }, [isValidating])

  useEffect(() => {
    if (props.disabled !== 'function') { setDisabled(props.disabled) } //As react cannot store function directly https://medium.com/swlh/how-to-store-a-function-with-the-usestate-hook-in-react-8a88dd4eede1
  }, [props.disabled])

  const setSubmitButtonProps = useCallback((submitProps) => {
    props.setSubmitButtonProps && props.setSubmitButtonProps(submitProps);
  }, [])

  useEffect(() => {
    setSubmitButtonProps && setSubmitButtonProps(submitButtonProps)
  }, [])

  useEffect(() => {
    setErrors({...errors, ...props.errors})
  }, [props.errors])

  useEffect(() => {
    setFormData(defaultObject)
  }, [defaultObject])

  return {formData, setFormData, getInputProps, formProps, submitButtonProps, useParentFormRef, setDisabled, validated, handleInputChange}
}


useForm.propTypes = {
  defaultObject: PropTypes.object,
  initialValues: PropTypes.object.isRequired,
  validationSchema: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  preProcess: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
}

useForm.defaultProps = {
  errors: {}
}
